import { Box, Button } from '@chronosphereio/chrono-ui';
import { useFeatureFlags } from '@chronosphereio/core';
import { produce } from 'immer';
import { merge } from 'lodash';
import { ArrowExpand as ExpandIcon, Plus } from 'mdi-material-ui';
import { ComponentProps, forwardRef, memo, useMemo, useState } from 'react';
import {
  ContentDimensions,
  ContextLinkDefinition,
  getQueriesWithContent,
  PanelDefinition,
  SeriesMenuItemOptions,
  UnknownSpec,
} from '@/core';
import { useProcessPanelDefinition } from '@/dashboards/utils/panelUtils';
import { TimeSeriesChartVizProps, TooltipContextMenuOptions } from '@/panels-plugin';
import {
  PanelChromeProps,
  useComputeContextLinkType,
  useExpandPanelContext,
  useLinksFromData,
  usePlugin,
  useUpdateExpandPanelFocus,
} from '@/plugin-system';
import { PanelGroupItemId, useDashboardAnnotationConfig } from '../../context';
import { DashboardEditActionsProps, PanelEditActions } from './PanelEditActions';
import { PanelLinks } from './PanelLinks';
import { PanelQueryInfo, type PanelQueryInfoProps } from './PanelQueryInfo';

// Using partial `ContentDimensions` here because in some contexts (e.g. modal, edit panel), they cannot be inferred
// from react-grid-layout, and we need to fall back to not setting them, so that `VizPanel` will determine them using a
// `ResponsiveWrapper`.
export interface PanelProps extends Partial<ContentDimensions> {
  definition: PanelDefinition;
  draggable?: boolean;
  editHandlers?: DashboardEditActionsProps['editHandlers'];
  panelGroupItemId?: PanelGroupItemId;
  panelKey?: string;
  panelOptions?: PanelOptions;
  onDefinitionChange?: (definition: PanelDefinition) => void;
  renderTitle?: (title: string) => string;
  mode?: 'edit' | 'view';
  /**
   * If panel kind is TimeSeriesChart, these props will be forwarded to the time series chart component.
   */
  timeSeriesChartProps?: Partial<TimeSeriesChartVizProps>;
  gridLayout?: { w: number; h: number };
}

export type PanelQueryInfoAnalyticsCallbacks = Pick<PanelQueryInfoProps, 'onCopyPanelQuery'>;

export interface PanelOptions extends Pick<PanelChromeProps, 'panelInspectorContent'> {
  /**
   * Content to render in the top-right corner of the panel. It will only be
   * rendered when the panel is in edit mode.
   */
  extra?: (props: PanelExtraProps) => React.ReactNode;

  /**
   * Optional analytics for copying a query from the panel header "view query" popover (e.g. cloud dashboards).
   */
  getPanelQueryInfoCallbacks?: (props: PanelExtraProps) => PanelQueryInfoAnalyticsCallbacks | undefined;

  /**
   * Menu items to render in the `VizPanel` header menu.
   */
  menuItems?: (props: PanelExtraProps) => React.ReactNode;
  /**
   * A callback to include additional menu items for a series.
   */
  getSeriesMenuOptions?: TooltipContextMenuOptions['seriesMenuItems'];
  /**
   * Custom render function to override the default Panel component.
   */
  renderPanel?: (props: PanelProps) => React.ReactNode;
}

export type PanelExtraProps = {
  /**
   * The PanelDefinition for the panel.
   */
  panelDefinition?: PanelDefinition;
  /**
   * The PanelGroupItemId for the panel.
   */
  panelGroupItemId?: PanelGroupItemId;
  /**
   * The panel key for the panel.
   */
  panelKey?: string;
};

/**
 * Renders a PanelDefinition's content inside of a VizPanel.
 */
const PanelComponent = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  {
    definition,
    editHandlers,
    onDefinitionChange,
    panelGroupItemId,
    panelKey,
    panelOptions,
    draggable,
    mode = 'view',
    renderTitle = (t) => t,
    width,
    height,
    timeSeriesChartProps,
  },
  ref
) {
  const isEditMode = mode === 'edit';
  const { data: plugin, isLoading: isPanelPluginLoading } = usePlugin('Panel', definition.spec.plugin.kind, {
    useErrorBoundary: true,
  });
  const PanelContent = plugin?.PanelComponent;
  if (!isPanelPluginLoading && PanelContent === undefined) {
    throw new Error(`Missing PanelComponent from panel plugin for kind '${definition.spec.plugin.kind}'`);
  }

  const handlePluginSpecChange = (spec: UnknownSpec, options?: { mergeUpdates?: boolean }) => {
    if (onDefinitionChange) {
      onDefinitionChange(
        produce(definition, (draft: PanelDefinition) => {
          if (options?.mergeUpdates) {
            // Option to deep merge partial spec update into existing spec
            draft.spec.plugin.spec = merge({}, draft.spec.plugin.spec, spec);
          } else {
            // Replace entire spec by default
            draft.spec.plugin.spec = spec;
          }
        })
      );
    }
  };

  const onCreateAnnotation = useDashboardAnnotationConfig();

  const allLinks = useCombinedContextLinks(definition.spec.links);

  const { scopedLinks, panelLinks } = useComputeContextLinkType(allLinks, plugin?.contextLinkConfig);

  const { interpolatedPanelDefinition } = useProcessPanelDefinition({
    panelDefinition: definition,
  });
  const title = renderTitle(interpolatedPanelDefinition.spec.display.name ?? '');

  // Currently we only use enableDashboardSafeMode internally as a dev flag so we can load dashboards
  // even if they are too slow to render. This is useful for debugging and testing.
  const { enableDashboardSafeMode, enableMutableDashboardAnnotations, enableDashboardPanelViewQueriesPopover } =
    useFeatureFlags();

  const actions = [];
  const hasLinks = panelLinks.length > 0;
  const hasExtraOptions =
    panelOptions?.extra?.({ panelDefinition: definition, panelGroupItemId, panelKey }) !== undefined;

  if (hasLinks) {
    actions.push(<PanelLinks links={panelLinks} key="links" />);
  }
  // Only render query info popover if there are queries with content.
  // Avoids showing the code icon for Markdown panels.
  if (enableDashboardPanelViewQueriesPopover) {
    const queriesWithContent = getQueriesWithContent(definition.spec.queries);
    if (queriesWithContent.length > 0) {
      const queryInfoCallbacks = panelOptions?.getPanelQueryInfoCallbacks?.({
        panelDefinition: definition,
        panelGroupItemId,
        panelKey,
      });
      // Use definition.spec.queries to avoid losing the original index for the queries (A, B, C, etc.).
      actions.push(
        <PanelQueryInfo
          key="query-info"
          queries={definition.spec.queries}
          onCopyPanelQuery={queryInfoCallbacks?.onCopyPanelQuery}
        />
      );
    }
  }
  if (hasExtraOptions) {
    const extraOptionsComponent = panelOptions?.extra?.({ panelDefinition: definition, panelGroupItemId, panelKey });
    actions.push(extraOptionsComponent);
  }

  const menuItems = panelOptions?.menuItems?.({ panelDefinition: definition, panelGroupItemId, panelKey });
  const [disableRender, setDisableRender] = useState(enableDashboardSafeMode);

  const { mode: expandPanelMode, focusedItemKey, expandedPanelKey } = useExpandPanelContext();
  const handleOnSeriesExpand = useUpdateExpandPanelFocus(panelKey);

  if (!PanelContent) {
    // This should only happen very briefly while the panel plugin is loading.
    // Otherwise, the PanelContent missing gets caught by an error handler above.
    return null;
  }

  if (disableRender) {
    return (
      <Box border={1} borderColor={(t) => t.palette.divider} padding={1} height={'100%'}>
        <div>{title}</div>
        <Button
          variant="secondary"
          onClick={() => {
            setDisableRender(false);
          }}
        >
          Click to Render
        </Button>
      </Box>
    );
  }

  const commonPanelContentProps: ComponentProps<typeof PanelContent> = {
    links: scopedLinks,
    onPluginSpecChange: handlePluginSpecChange,
    panelKey,
    spec: definition.spec.plugin.spec,
    width,
    height,
    panelChromeProps: {
      panelRef: ref,
      // If in edit mode and panel has no title, set title to an empty string to ensure the panel has a header
      // This prevents the panel actions from rendering on top of the panel group actions
      title: editHandlers && !title ? ' ' : title,
      description: definition.spec.display.description,
      actions: editHandlers ? (
        <PanelEditActions title={title} editHandlers={editHandlers} />
      ) : (
        actions.length > 0 && actions
      ),
      actionsVisiblity: isEditMode ? 'always' : 'hover',
      isLoading: isPanelPluginLoading,
      errorBoundaryResetKeys: [definition.spec.plugin.spec],
      menuItems: menuItems,
      draggable: draggable && isEditMode,
      panelInspectorContent: panelOptions?.panelInspectorContent,
      showRelativeTimeChip: definition.spec.query_options?.time?.start !== undefined,
    },
  };

  // A workaround until we figure out a way to add support for types in the plugin system
  if (definition.spec.plugin.kind === 'TimeSeriesChart') {
    const expandPanelProps: Partial<TimeSeriesChartVizProps> = {
      tooltipContextMenu: {
        seriesMenuItems: (ctx, series) => {
          const options: SeriesMenuItemOptions[] = panelOptions?.getSeriesMenuOptions
            ? panelOptions?.getSeriesMenuOptions(ctx, series)
            : [];

          return [
            {
              title: 'View in full screen',
              description: 'expand series in full screen view',
              icon: ExpandIcon,
              onClick: () => {
                handleOnSeriesExpand?.(ctx.seriesKey);
              },
            },
            ...options,
          ];
        },
        menuItems: (ctx) => {
          const items = [];
          const timestamp = ctx.timestamp;

          if (timestamp && onCreateAnnotation) {
            items.push({
              title: 'Create annotation event',
              icon: Plus,
              onClick: () => {
                const labels: Record<string, string> = {};
                if (panelKey && !enableMutableDashboardAnnotations) {
                  // mutable annotations have first class support for scoping annotations to a panel,
                  // so no need to put panel key in labels
                  labels['dashboard_panel_key'] = panelKey;
                }
                onCreateAnnotation({
                  timestamp: timestamp,
                  labels,
                });
              },
            });
          }

          return items;
        },
      },
    };

    const isExpandPanelEnabled = expandedPanelKey && expandedPanelKey === panelKey;
    const expandPanelPropsForChart: Partial<TimeSeriesChartVizProps> = {
      focusedSeriesId: focusedItemKey,
    };

    const timeSeriesPanelProps = {
      ...commonPanelContentProps,
      ...timeSeriesChartProps,
      ...(expandedPanelKey === undefined && expandPanelMode === 'tooltip' ? expandPanelProps : {}),
      ...(isExpandPanelEnabled ? expandPanelPropsForChart : {}),
    };

    return <PanelContent {...timeSeriesPanelProps} />;
  }

  return <PanelContent {...commonPanelContentProps} />;
});

export const Panel = memo(PanelComponent);
/**
 * Combines the links from the panel definition with the links from the data
 */
function useCombinedContextLinks(links?: ContextLinkDefinition[]): ContextLinkDefinition[] {
  const linksFromData = useLinksFromData();
  return useMemo(() => {
    return [...(links ?? []), ...linksFromData];
  }, [links, linksFromData]);
}
